import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, Form, Modal, Spinner, Table } from "react-bootstrap";
import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import {
  FaArrowDown,
  FaArrowUp,
  FaEdit,
  FaEye,
  FaSort,
  FaSortDown,
  FaSortUp,
  FaTrash,
} from "react-icons/fa";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import Select from "react-select";
import funcFetch from "../../../../Utilities/funcFetch";
import useFetchGet from "../../../../Utilities/useFetchGet";
import "./ClaimDocuments.css";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url
).toString();

const getDocumentPreviewType = (document) => {
  const mimeType = String(
    document.mimeType ||
      document.contentType ||
      document.content_type ||
      document.fileType ||
      ""
  )
    .split(";")[0]
    .trim()
    .toLowerCase();
  if (mimeType === "application/pdf") {
    return "pdf";
  }
  if (mimeType.startsWith("image/")) {
    return "image";
  }

  const documentPaths = [document.name, document.url].map((path) =>
    String(path || "")
      .split(/[?#]/)[0]
      .toLowerCase()
  );
  if (documentPaths.some((path) => path.endsWith(".pdf"))) {
    return "pdf";
  }
  if (
    documentPaths.some((path) =>
      /\.(avif|bmp|gif|jpe?g|png|svg|webp)$/.test(path)
    )
  ) {
    return "image";
  }
  return "unsupported";
};

const formatDocumentDate = (dateValue) => {
  if (!dateValue) {
    return "";
  }

  const dateOnlyMatch =
    typeof dateValue === "string" && dateValue.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const date = dateOnlyMatch
    ? new Date(
        Number(dateOnlyMatch[1]),
        Number(dateOnlyMatch[2]) - 1,
        Number(dateOnlyMatch[3])
      )
    : new Date(dateValue);

  return Number.isNaN(date.getTime())
    ? String(dateValue)
    : date.toLocaleDateString();
};

const formatDocumentWorkflows = (workflows) =>
  Array.isArray(workflows) ? workflows.join(", ") : workflows ?? "";

const getDocumentBlobType = (document, responseBlob) => {
  const responseType = String(responseBlob.type || "")
    .split(";")[0]
    .trim()
    .toLowerCase();
  if (getDocumentPreviewType(document) === "pdf") {
    return "application/pdf";
  }
  if (responseType.startsWith("image/")) {
    return responseType;
  }

  const documentPath = String(document.name || document.url || "")
    .split(/[?#]/)[0]
    .toLowerCase();
  const imageTypes = {
    ".avif": "image/avif",
    ".bmp": "image/bmp",
    ".gif": "image/gif",
    ".jpeg": "image/jpeg",
    ".jpg": "image/jpeg",
    ".png": "image/png",
    ".svg": "image/svg+xml",
    ".webp": "image/webp",
  };
  const imageExtension = Object.keys(imageTypes).find((extension) =>
    documentPath.endsWith(extension)
  );
  return imageExtension ? imageTypes[imageExtension] : responseType;
};

const getDocumentAuthErrorMessage = (authError) =>
  /AADSTS65001|consent_required|admin consent/i.test(
    `${authError?.errorCode || ""} ${authError?.message || ""}`
  )
    ? "Azure Storage access has not been consented for this app. An administrator must grant the Azure Storage user_impersonation permission."
    : "The document could not be opened. Check your Blob Storage access and try again.";

const ClaimDocuments = ({ debtorid, documents = [], onUploaded }) => {
  const { REACT_APP_API_URL } = process.env;
  const { instance, accounts } = useMsal();
  const {
    data: fileTypesResponse,
    error: fileTypesError,
  } = useFetchGet(`${REACT_APP_API_URL}/claims/GetFileTypes`);
  const fileInputRef = useRef(null);
  const pdfViewerRef = useRef(null);
  const mergePdfViewerRef = useRef(null);
  const previewUrlsRef = useRef([]);
  const mergePreviewObjectUrlRef = useRef("");
  const mergePreviewRequestRef = useRef(0);
  const [documentList, setDocumentList] = useState(documents);
  const [selectedDocumentUrl, setSelectedDocumentUrl] = useState("");
  const [documentPreviewUrl, setDocumentPreviewUrl] = useState("");
  const [documentPreviewLoading, setDocumentPreviewLoading] = useState(false);
  const [documentPreviewError, setDocumentPreviewError] = useState("");
  const [documentPreviewAuthReady, setDocumentPreviewAuthReady] = useState(false);
  const [pdfPageCount, setPdfPageCount] = useState(0);
  const [pdfCurrentPage, setPdfCurrentPage] = useState(1);
  const [pdfPageWidth, setPdfPageWidth] = useState(640);
  const [imagePreviewError, setImagePreviewError] = useState(false);
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [deletingDocument, setDeletingDocument] = useState(null);
  const [documentToUpdate, setDocumentToUpdate] = useState(null);
  const [newDocumentName, setNewDocumentName] = useState("");
  const [updatedFileType, setUpdatedFileType] = useState("");
  const [updatingDocument, setUpdatingDocument] = useState(false);
  const [documentUpdateError, setDocumentUpdateError] = useState("");
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [documentsToMerge, setDocumentsToMerge] = useState([]);
  const [mergingDocuments, setMergingDocuments] = useState(false);
  const [mergePreviewDocument, setMergePreviewDocument] = useState(null);
  const [mergePreviewUrl, setMergePreviewUrl] = useState("");
  const [mergePreviewLoading, setMergePreviewLoading] = useState(false);
  const [mergePreviewError, setMergePreviewError] = useState("");
  const [mergePreviewPageCount, setMergePreviewPageCount] = useState(0);
  const [mergePreviewCurrentPage, setMergePreviewCurrentPage] = useState(1);
  const [mergePreviewPageWidth, setMergePreviewPageWidth] = useState(500);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const fileTypes = Array.isArray(fileTypesResponse)
    ? fileTypesResponse
    : fileTypesResponse?.FileType;
  const fileTypeOptions = (Array.isArray(fileTypes) ? fileTypes : [])
    .map((fileType) => {
      const value =
        typeof fileType === "string"
          ? fileType
          : fileType?.fileType ?? fileType?.FileType;
      return value == null
        ? null
        : { value: String(value), label: String(value) };
    })
    .filter(Boolean);
  const documentUpdateFileTypeOptions =
    updatedFileType &&
    !fileTypeOptions.some((option) => option.value === updatedFileType)
      ? [...fileTypeOptions, { value: updatedFileType, label: updatedFileType }]
      : fileTypeOptions;
  const selectedDocument = documentList.find(
    (document) => document.url === selectedDocumentUrl
  );
  const selectedDocumentPreviewType = selectedDocument
    ? getDocumentPreviewType(selectedDocument)
    : null;
  const sortedDocumentList = sortField
    ? [...documentList].sort((firstDocument, secondDocument) => {
        const firstRawValue = firstDocument[sortField];
        const secondRawValue = secondDocument[sortField];
        if (sortField === "dateAdded") {
          const firstDate =
            firstRawValue == null || firstRawValue === ""
              ? NaN
              : new Date(firstRawValue).getTime();
          const secondDate =
            secondRawValue == null || secondRawValue === ""
              ? NaN
              : new Date(secondRawValue).getTime();
          if (Number.isFinite(firstDate) && Number.isFinite(secondDate)) {
            const dateComparison = firstDate - secondDate;
            return sortDirection === "asc"
              ? dateComparison
              : -dateComparison;
          }
          if (Number.isFinite(firstDate) !== Number.isFinite(secondDate)) {
            const dateComparison = Number.isFinite(firstDate) ? 1 : -1;
            return sortDirection === "asc"
              ? dateComparison
              : -dateComparison;
          }
        }

        const firstValue = Array.isArray(firstRawValue)
          ? firstRawValue.join(", ")
          : String(firstRawValue ?? "");
        const secondValue = Array.isArray(secondRawValue)
          ? secondRawValue.join(", ")
          : String(secondRawValue ?? "");
        const comparison = firstValue.localeCompare(secondValue, undefined, {
          numeric: true,
          sensitivity: "base",
        });
        return sortDirection === "asc" ? comparison : -comparison;
      })
    : documentList;

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection((direction) => (direction === "asc" ? "desc" : "asc"));
      return;
    }

    setSortField(field);
    setSortDirection("asc");
  };

  const sortIcon = (field) => {
    if (sortField !== field) {
      return <FaSort aria-hidden="true" />;
    }
    return sortDirection === "asc" ? (
      <FaSortUp aria-hidden="true" />
    ) : (
      <FaSortDown aria-hidden="true" />
    );
  };

  const sortableHeader = (label, field) => (
    <th
      scope="col"
      aria-sort={
        sortField === field
          ? sortDirection === "asc"
            ? "ascending"
            : "descending"
          : "none"
      }
    >
      <Button
        variant="link"
        className="claim-documents-sort-button p-0"
        onClick={() => handleSort(field)}
      >
        {label} <span className="ms-1">{sortIcon(field)}</span>
      </Button>
    </th>
  );

  const getDocumentAccessToken = useCallback(
    async (url, allowInteraction) => {
      const documentUrl = new URL(url, window.location.href);
      const apiUrl = new URL(REACT_APP_API_URL, window.location.origin);
      const isApplicationApi = documentUrl.origin === apiUrl.origin;
      if (!isApplicationApi && documentUrl.searchParams.has("sig")) {
        return null;
      }

      const account = instance.getActiveAccount() || accounts[0];
      if (!account) {
        throw new Error("No signed-in account is available for document access.");
      }

      const request = {
        scopes: [
          isApplicationApi
            ? "api://0545cd28-732c-4262-9c0e-bdbf335e2b6c/User.Read"
            : "https://storage.azure.com/user_impersonation",
        ],
        account,
      };

      try {
        const tokenResponse = await instance.acquireTokenSilent(request);
        return tokenResponse.accessToken;
      } catch (tokenError) {
        if (
          !allowInteraction ||
          !(tokenError instanceof InteractionRequiredAuthError)
        ) {
          throw tokenError;
        }

        const tokenResponse = await instance.acquireTokenPopup({
          ...request,
          prompt: "consent",
        });
        return tokenResponse.accessToken;
      }
    },
    [REACT_APP_API_URL, accounts, instance]
  );

  const fetchDocumentBlob = useCallback(
    async (url, allowInteraction = false) => {
      const documentUrl = new URL(url, window.location.href);
      const apiUrl = new URL(REACT_APP_API_URL, window.location.origin);
      const isApplicationApi = documentUrl.origin === apiUrl.origin;
      const headers = new Headers();
      const accessToken = await getDocumentAccessToken(
        documentUrl.href,
        allowInteraction
      );
      if (accessToken) {
        headers.set("Authorization", `Bearer ${accessToken}`);
      }
      if (!isApplicationApi && accessToken) {
        headers.set("x-ms-version", "2023-11-03");
      }

      const response = await fetch(documentUrl.href, { headers });
      if (!response.ok) {
        throw new Error(`Document request failed (${response.status}).`);
      }
      return response.blob();
    },
    [REACT_APP_API_URL, getDocumentAccessToken]
  );

  const renderDocumentPreviewError = () => (
    <Alert variant="warning">
      {documentPreviewError || "The document preview is unavailable."}
    </Alert>
  );

  const handleSelectDocument = async (event, document) => {
    event.preventDefault();
    event.stopPropagation();
    setDocumentPreviewError("");
    setDocumentPreviewLoading(true);
    setDocumentPreviewAuthReady(false);
    setSelectedDocumentUrl(document.url);

    if (!["pdf", "image"].includes(getDocumentPreviewType(document))) {
      setDocumentPreviewAuthReady(true);
      setDocumentPreviewLoading(false);
      return;
    }

    try {
      await getDocumentAccessToken(document.url, true);
      setDocumentPreviewAuthReady(true);
    } catch (authError) {
      console.error("Document preview authorization failed", authError);
      setDocumentPreviewError(getDocumentAuthErrorMessage(authError));
      setDocumentPreviewLoading(false);
    }
  };

  useEffect(
    () => () => {
      previewUrlsRef.current.forEach((previewUrl) =>
        URL.revokeObjectURL(previewUrl)
      );
      if (mergePreviewObjectUrlRef.current) {
        URL.revokeObjectURL(mergePreviewObjectUrlRef.current);
      }
    },
    []
  );

  useEffect(() => {
    let cancelled = false;
    let objectUrl = "";

    setDocumentPreviewUrl("");
    setDocumentPreviewError("");
    setImagePreviewError(false);
    setPdfPageCount(0);
    setPdfCurrentPage(1);
    if (
      !selectedDocument ||
      !["pdf", "image"].includes(selectedDocumentPreviewType)
    ) {
      setDocumentPreviewLoading(false);
      return undefined;
    }
    if (!documentPreviewAuthReady) {
      return undefined;
    }

    const loadDocumentPreview = async () => {
      setDocumentPreviewLoading(true);
      try {
        const responseBlob = await fetchDocumentBlob(selectedDocument.url);
        const documentBlob = new Blob([responseBlob], {
          type: getDocumentBlobType(selectedDocument, responseBlob),
        });
        objectUrl = URL.createObjectURL(documentBlob);
        if (cancelled) {
          URL.revokeObjectURL(objectUrl);
        } else {
          setDocumentPreviewUrl(objectUrl);
        }
      } catch (previewError) {
        console.error("Document preview failed", previewError);
        if (!cancelled) {
          setDocumentPreviewError(getDocumentAuthErrorMessage(previewError));
        }
      } finally {
        if (!cancelled) {
          setDocumentPreviewLoading(false);
        }
      }
    };

    loadDocumentPreview();
    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [
    documentPreviewAuthReady,
    fetchDocumentBlob,
    selectedDocument,
    selectedDocumentPreviewType,
  ]);

  useEffect(() => {
    const viewer = pdfViewerRef.current;
    if (!viewer || typeof ResizeObserver === "undefined") {
      return undefined;
    }

    const resizeObserver = new ResizeObserver(([entry]) => {
      setPdfPageWidth(Math.max(200, Math.floor(entry.contentRect.width - 32)));
    });
    resizeObserver.observe(viewer);
    return () => resizeObserver.disconnect();
  }, [selectedDocumentUrl, documentPreviewUrl]);

  useEffect(() => {
    const viewer = mergePdfViewerRef.current;
    if (!showMergeModal || !viewer || typeof ResizeObserver === "undefined") {
      return undefined;
    }

    const resizeObserver = new ResizeObserver(([entry]) => {
      setMergePreviewPageWidth(
        Math.max(200, Math.floor(entry.contentRect.width - 32))
      );
    });
    resizeObserver.observe(viewer);
    return () => resizeObserver.disconnect();
  }, [showMergeModal, mergePreviewUrl]);

  useEffect(() => {
    setDocumentList(documents);
  }, [documents]);

  const revokePreviewUrls = () => {
    previewUrlsRef.current.forEach((previewUrl) =>
      URL.revokeObjectURL(previewUrl)
    );
    previewUrlsRef.current = [];
  };

  const handleFileChange = (event) => { 
    revokePreviewUrls();
    const selectedFiles = Array.from(event.target.files || []).map((file) => {
      const previewUrl = URL.createObjectURL(file);
      previewUrlsRef.current.push(previewUrl);

      return {
        file,
        fileType: "",
        newName: "",
        previewUrl,
      };
    });
    setFiles(
      selectedFiles
    );
    setSuccess("");
    setError("");
  };

  const handleEditDocument = (document) => {
    setDocumentToUpdate(document);
    setNewDocumentName(document.name || "");
    setUpdatedFileType(document.fileType || "");
    setDocumentUpdateError("");
    setSuccess("");
  };

  const handleDeleteDocument = async (document) => {
    if (!window.confirm(`Delete "${document.name}"?`)) {
      return;
    }
    if (!debtorid) {
      setError("Debtor ID is required before documents can be deleted.");
      return;
    }

    setDeletingDocument(document.name);
    setError("");
    setSuccess("");

    try {
      const updatedDocuments = await funcFetch(
        `${REACT_APP_API_URL}/claims/DeleteDocument`,
        {
          debtorid: encodeURIComponent(String(debtorid)),
          document: encodeURIComponent(document.name),
        },
        "DELETE",
        null,
        instance,
        accounts
      );

      if (!Array.isArray(updatedDocuments)) {
        throw new Error("DeleteDocument did not return a document list.");
      }

      setDocumentList(updatedDocuments);
      setSuccess("Document deleted successfully.");
    } catch (deleteError) {
      console.error("Document deletion failed", deleteError);
      setError("The document could not be deleted. Please try again.");
    } finally {
      setDeletingDocument(null);
    }
  };

  const handleUpdateDocument = async () => {
    const trimmedNewName = newDocumentName.trim();
    const selectedFileType = updatedFileType;
    if (!debtorid || !documentToUpdate || !trimmedNewName) {
      setDocumentUpdateError("Enter a new file name before updating.");
      return;
    }

    setUpdatingDocument(true);
    setDocumentUpdateError("");
    setSuccess("");

    try {
      await funcFetch(
        `${REACT_APP_API_URL}/claims/PatchDocument`,
        {
          debtorid: encodeURIComponent(String(debtorid)),
          oldname: encodeURIComponent(documentToUpdate.name),
          newname: encodeURIComponent(trimmedNewName),
          fileType: encodeURIComponent(selectedFileType),
        },
        "PATCH",
        null,
        instance,
        accounts
      );
      await onUploaded?.(debtorid);
      setDocumentToUpdate(null);
      setSuccess("Document updated successfully.");
    } catch (updateError) {
      console.error("Document update failed", updateError);
      setDocumentUpdateError("The document could not be updated. Please try again.");
    } finally {
      setUpdatingDocument(false);
    }
  };

  const handleUpload = async () => {
    if (!debtorid) {
      setError("Debtor ID is required before documents can be uploaded.");
      return;
    }

    if (files.length === 0) {
      setError("Select at least one document to upload.");
      return;
    }

    setUploading(true);
    setSuccess("");
    setError("");

    try {
      const tokenResponse = await instance.acquireTokenSilent({
        scopes: ["api://0545cd28-732c-4262-9c0e-bdbf335e2b6c/User.Read"],
        account: instance.getActiveAccount(),
      });
      const formData = new FormData();
      files.forEach(({ file, fileType, newName }, index) => {
        formData.append(`documents[${index}].fileType`, fileType);
        formData.append(`documents[${index}].document`, file, file.name);
        formData.append(`documents[${index}].NewName`, newName);
      });

      const response = await fetch(
        `${REACT_APP_API_URL}/claims/uploadDocs?debtorid=${encodeURIComponent(debtorid)}`,
        {
        method: "POST",
        headers: { Authorization: `Bearer ${tokenResponse.accessToken}` },
        body: formData,
        }
      );

      if (!response.ok) {
        throw new Error(`Upload failed with status ${response.status}.`);
      }

      await onUploaded?.(debtorid);
      revokePreviewUrls();
      setFiles([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      setSuccess("Document upload complete.");
    } catch (uploadError) {
      console.error("Document upload failed", uploadError);
      setError("The documents could not be uploaded. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const clearMergePreview = () => {
    mergePreviewRequestRef.current += 1;
    if (mergePreviewObjectUrlRef.current) {
      URL.revokeObjectURL(mergePreviewObjectUrlRef.current);
      mergePreviewObjectUrlRef.current = "";
    }
    setMergePreviewDocument(null);
    setMergePreviewUrl("");
    setMergePreviewLoading(false);
    setMergePreviewError("");
    setMergePreviewPageCount(0);
    setMergePreviewCurrentPage(1);
  };

  const handlePreviewMergeDocument = async (document) => {
    const requestId = mergePreviewRequestRef.current + 1;
    mergePreviewRequestRef.current = requestId;
    if (mergePreviewObjectUrlRef.current) {
      URL.revokeObjectURL(mergePreviewObjectUrlRef.current);
      mergePreviewObjectUrlRef.current = "";
    }
    setMergePreviewDocument(document);
    setMergePreviewUrl("");
    setMergePreviewError("");
    setMergePreviewPageCount(0);
    setMergePreviewCurrentPage(1);

    if (!["pdf", "image"].includes(getDocumentPreviewType(document))) {
      setMergePreviewLoading(false);
      setMergePreviewError("Preview is available for PDF and image files.");
      return;
    }

    setMergePreviewLoading(true);
    try {
      const responseBlob = await fetchDocumentBlob(document.url, true);
      if (requestId !== mergePreviewRequestRef.current) {
        return;
      }

      const previewBlob = new Blob([responseBlob], {
        type: getDocumentBlobType(document, responseBlob),
      });
      const objectUrl = URL.createObjectURL(previewBlob);
      mergePreviewObjectUrlRef.current = objectUrl;
      setMergePreviewUrl(objectUrl);
    } catch (previewError) {
      console.error("Merge document preview failed", previewError);
      if (requestId === mergePreviewRequestRef.current) {
        setMergePreviewError(getDocumentAuthErrorMessage(previewError));
      }
    } finally {
      if (requestId === mergePreviewRequestRef.current) {
        setMergePreviewLoading(false);
      }
    }
  };

  const openMergeModal = () => {
    setDocumentsToMerge([]);
    setError("");
    setSuccess("");
    clearMergePreview();
    setShowMergeModal(true);
  };

  const addDocumentToMerge = (document) => {
    setDocumentsToMerge((selectedDocuments) => [
      ...selectedDocuments,
      document,
    ]);
    handlePreviewMergeDocument(document);
  };

  const removeDocumentFromMerge = (documentIndex) => {
    const removedDocument = documentsToMerge[documentIndex];
    const updatedDocuments = documentsToMerge.filter(
      (_, index) => index !== documentIndex
    );
    setDocumentsToMerge(updatedDocuments);
    if (mergePreviewDocument?.url === removedDocument?.url) {
      if (updatedDocuments.length > 0) {
        handlePreviewMergeDocument(updatedDocuments[0]);
      } else {
        clearMergePreview();
      }
    }
  };

  const moveDocumentToMerge = (documentIndex, direction) => {
    const destinationIndex = documentIndex + direction;
    setDocumentsToMerge((selectedDocuments) => {
      if (
        destinationIndex < 0 ||
        destinationIndex >= selectedDocuments.length
      ) {
        return selectedDocuments;
      }

      // The selected-document sequence is the order used in the merged output.
      const reorderedDocuments = [...selectedDocuments];
      [reorderedDocuments[documentIndex], reorderedDocuments[destinationIndex]] =
        [reorderedDocuments[destinationIndex], reorderedDocuments[documentIndex]];
      return reorderedDocuments;
    });
  };

  const handleMergeDocuments = async () => {
    if (!debtorid) {
      setError("Debtor ID is required before documents can be merged.");
      return;
    }

    if (documentsToMerge.length < 2) {
      setError("Select at least two documents to merge.");
      return;
    }

    setMergingDocuments(true);
    setError("");
    setSuccess("");

    try {
      await funcFetch(
        `${REACT_APP_API_URL}/claims/MergeDocuments`,
        { debtorId: debtorid },
        "POST",
        // MergeDocuments binds its request body directly to List<string>.
        documentsToMerge.map((document) => document.name),
        instance,
        accounts
      );
      await onUploaded?.(debtorid);
      setShowMergeModal(false);
      setDocumentsToMerge([]);
      clearMergePreview();
      setSuccess("Documents merged successfully.");
    } catch (mergeError) {
      console.error("Document merge failed", mergeError);
      setError("The documents could not be merged. Please try again.");
    } finally {
      setMergingDocuments(false);
    }
  };

  const uploadControls = (
    <>
      {fileTypesError && (
        <Alert className="mt-3" variant="warning">
          The file types could not be loaded. File type selection may be unavailable.
        </Alert>
      )}
      <Form.Group controlId="claim-document-upload" className="mb-3">
        <Form.Label>Upload documents</Form.Label>
        <Form.Control
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleFileChange}
          disabled={!debtorid || uploading}
        />
      </Form.Group>
      {files.length > 0 && (
        <Table className="mb-3" striped bordered responsive>
          <thead>
            <tr>
              <th>Selected file</th>
              <th>File type</th>
              <th>New Name</th>
            </tr>
          </thead>
          <tbody>
            {files.map(({ file, fileType, newName, previewUrl }, index) => (
              <tr key={`${file.name}-${file.lastModified}-${index}`}>
                <td>
                  {file.name}
                  <Button
                    as="a"
                    href={previewUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="link"
                    className="p-0 ms-2"
                    aria-label={`Preview ${file.name}`}
                    title="Preview file"
                  >
                    <FaEye aria-hidden="true" />
                  </Button>
                </td>
                <td>
                  <Select
                    options={fileTypeOptions}
                    value={
                      fileTypeOptions.find(
                        (option) => option.value === fileType
                      ) || null
                    }
                    aria-label={`File type for ${file.name}`}
                    onChange={(option) => {
                      const nextFileType = option?.value || "";
                      setFiles((currentFiles) =>
                        currentFiles.map((currentFile, currentIndex) =>
                          currentIndex === index
                            ? { ...currentFile, fileType: nextFileType }
                            : currentFile
                        )
                      );
                    }}
                    isSearchable
                    isClearable
                    placeholder="Select file type"
                    isDisabled={uploading}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 1060 }),
                    }}
                  />
                </td>
                <td>
                  <Form.Control
                    aria-label={`New name for ${file.name}`}
                    value={newName}
                    onChange={(event) => {
                      const nextNewName = event.target.value;
                      setFiles((currentFiles) =>
                        currentFiles.map((currentFile, currentIndex) =>
                          currentIndex === index
                            ? { ...currentFile, newName: nextNewName }
                            : currentFile
                        )
                      );
                    }}
                    disabled={uploading}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      <div className="claim-document-action-row mb-3">
        <Button onClick={handleUpload} disabled={!debtorid || uploading}>
          {uploading ? <Spinner animation="border" size="sm" /> : "Upload"}
        </Button>
        {documentList.length > 0 && (
          <Button onClick={openMergeModal}>Merge documents</Button>
        )}
      </div>
      {success && <Alert className="mt-3" variant="success">{success}</Alert>}
      {error && <Alert className="mt-3" variant="warning">{error}</Alert>}
    </>
  );

  return (
    <>
      {documentList.length > 0 ? (
        <>
          <div className="claim-documents-layout mb-3">
            <section className="claim-documents-list" aria-label="Document list">
              {uploadControls}
              <Table className="mb-0" striped bordered hover responsive>
                <thead>
                  <tr>
                    {sortableHeader("Document", "name")}
                    {sortableHeader("File Type", "fileType")}
                    {sortableHeader("Added By", "addedBy")}
                    {sortableHeader("Date", "dateAdded")}
                    {sortableHeader("Source", "source")}
                    {sortableHeader("Workflows", "workflows")}
                  </tr>
                </thead>
                <tbody>
                  {sortedDocumentList.map((document, index) => (
                    <tr key={`${document.url}-${index}`}>
                      <td>
                        <a
                          className="claim-document-name"
                          href={document.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={`Open ${document.name} in a new tab`}
                        >
                          {document.name}
                        </a>
                        <Button
                          type="button"
                          variant="link"
                          className="p-0 ms-2"
                          onClick={(event) =>
                            handleSelectDocument(event, document)
                          }
                          aria-label={`Preview ${document.name}`}
                          title="Preview document"
                        >
                          <FaEye aria-hidden="true" />
                        </Button>
                        <Button
                          variant="link"
                          className="p-0 ms-2"
                          onClick={() => handleEditDocument(document)}
                          aria-label={`Rename ${document.name}`}
                          title="Update document"
                        >
                          <FaEdit aria-hidden="true" />
                        </Button>
                        <Button
                          variant="link"
                          className="p-0 ms-2 text-danger"
                          onClick={() => handleDeleteDocument(document)}
                          disabled={deletingDocument !== null || updatingDocument}
                          aria-label={`Delete ${document.name}`}
                          title="Delete document"
                        >
                          {deletingDocument === document.name ? (
                            <Spinner animation="border" size="sm" />
                          ) : (
                            <FaTrash aria-hidden="true" />
                          )}
                        </Button>
                      </td>
                      <td>{document.fileType}</td>
                      <td>{document.addedBy}</td>
                      <td>{formatDocumentDate(document.dateAdded)}</td>
                      <td>{document.source}</td>
                      <td>{formatDocumentWorkflows(document.workflows)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </section>
            <section
              className="claim-documents-preview"
              aria-label="Document preview"
            >
              <h5>Doc Preview</h5>
              {selectedDocument ? (
                <>
                  <h6 className="claim-document-preview-title">
                    {selectedDocument.name}
                  </h6>
                  {selectedDocumentPreviewType === "image" ? (
                    documentPreviewLoading ? (
                      <div className="claim-document-preview-status">
                        <Spinner animation="border" role="status" />
                        <span className="ms-2">Loading image preview...</span>
                      </div>
                    ) : documentPreviewError || imagePreviewError ? (
                      <Alert variant="warning">
                        {documentPreviewError ||
                          "This image could not be displayed in the preview."}
                      </Alert>
                    ) : (
                      <img
                        className="claim-document-preview-image"
                        src={documentPreviewUrl}
                        alt={selectedDocument.name}
                        onError={() => setImagePreviewError(true)}
                      />
                    )
                  ) : selectedDocumentPreviewType === "pdf" ? (
                    documentPreviewLoading ? (
                      <div className="claim-document-preview-status">
                        <Spinner animation="border" role="status" />
                        <span className="ms-2">Loading PDF preview...</span>
                      </div>
                    ) : documentPreviewError ? (
                      renderDocumentPreviewError()
                    ) : documentPreviewUrl ? (
                      <div className="claim-document-pdf-viewer">
                        {pdfPageCount > 1 && (
                          <div className="claim-document-pdf-controls">
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() =>
                                setPdfCurrentPage((page) => Math.max(1, page - 1))
                              }
                              disabled={pdfCurrentPage <= 1}
                            >
                              Previous
                            </Button>
                            <span aria-live="polite">
                              Page {pdfCurrentPage} of {pdfPageCount}
                            </span>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() =>
                                setPdfCurrentPage((page) =>
                                  Math.min(pdfPageCount, page + 1)
                                )
                              }
                              disabled={pdfCurrentPage >= pdfPageCount}
                            >
                              Next
                            </Button>
                          </div>
                        )}
                        <div
                          className="claim-document-pdf-page"
                          ref={pdfViewerRef}
                        >
                          <Document
                            file={documentPreviewUrl}
                            onLoadSuccess={({ numPages }) => {
                              setPdfPageCount(numPages);
                              setPdfCurrentPage(1);
                              setDocumentPreviewError("");
                            }}
                            onLoadError={(loadError) => {
                              console.error("PDF document rendering failed", loadError);
                              setDocumentPreviewError(
                                "The PDF was downloaded but could not be rendered. It may be damaged, encrypted, or use an unsupported feature."
                              );
                            }}
                            loading={
                              <div className="claim-document-preview-status">
                                <Spinner animation="border" role="status" />
                                <span className="ms-2">
                                  Rendering PDF preview...
                                </span>
                              </div>
                            }
                            error={renderDocumentPreviewError()}
                          >
                            {pdfPageCount > 0 && (
                              <Page
                                pageNumber={pdfCurrentPage}
                                width={pdfPageWidth}
                              />
                            )}
                          </Document>
                        </div>
                      </div>
                    ) : (
                      renderDocumentPreviewError()
                    )
                  ) : (
                    <Alert variant="info">
                      Preview is available for PDF and image files.
                    </Alert>
                  )}
                </>
              ) : (
                <p className="claim-document-preview-empty">
                  Select a document to preview it here.
                </p>
              )}
            </section>
          </div>
        </>
      ) : (
        <section className="claim-documents-list" aria-label="Document upload">
          {uploadControls}
          <p className="mb-3">No files uploaded.</p>
        </section>
      )}
      <Modal
        show={Boolean(documentToUpdate)}
        onHide={() => {
          if (!updatingDocument) {
            setDocumentToUpdate(null);
          }
        }}
        centered
      >
        <Modal.Header closeButton={!updatingDocument}>
          <Modal.Title>Update Document</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Current File Name</Form.Label>
            <Form.Control value={documentToUpdate?.name || ""} readOnly />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>New Name</Form.Label>
            <Form.Control
              value={newDocumentName}
              onChange={(event) => setNewDocumentName(event.target.value)}
              disabled={updatingDocument}
              autoFocus
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>File Type</Form.Label>
            <Select
              options={documentUpdateFileTypeOptions}
              value={
                documentUpdateFileTypeOptions.find(
                  (option) => option.value === updatedFileType
                ) || null
              }
              onChange={(option) => {
                setUpdatedFileType(option?.value || "");
              }}
              isSearchable
              isClearable
              isDisabled={updatingDocument}
              placeholder="Select file type"
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 1060 }) }}
            />
          </Form.Group>
          {documentUpdateError && (
            <Alert className="mt-3 mb-0" variant="warning">
              {documentUpdateError}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setDocumentToUpdate(null)}
            disabled={updatingDocument}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdateDocument}
            disabled={updatingDocument || !newDocumentName.trim()}
          >
            {updatingDocument ? (
              <Spinner animation="border" size="sm" />
            ) : (
              "Update"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal
        show={showMergeModal}
        onHide={() => {
          if (!mergingDocuments) {
            setShowMergeModal(false);
            clearMergePreview();
          }
        }}
        centered
        scrollable
        size="xl"
      >
        <Modal.Header closeButton={!mergingDocuments}>
          <Modal.Title>Merge Documents</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Select documents to merge, then arrange them in the required order.</p>
          <div className="claim-document-merge-layout">
            <div className="claim-document-merge-list">
              <Form.Label>Available documents</Form.Label>
              <div className="mb-3">
                {documentList.map((document, index) => {
                  const selected = documentsToMerge.includes(document);
                  return (
                    <div
                      key={`${document.url}-${index}`}
                      className="d-flex align-items-center justify-content-between mb-2"
                    >
                      <span className="claim-document-merge-name">
                        {document.name}
                      </span>
                      <span className="claim-document-merge-actions">
                        <Button
                          size="sm"
                          variant="link"
                          className="p-1"
                          onClick={() =>
                            handlePreviewMergeDocument(document)
                          }
                          disabled={mergingDocuments}
                          aria-label={`Preview ${document.name}`}
                          title="Preview document"
                        >
                          <FaEye aria-hidden="true" />
                        </Button>
                        <Button
                          size="sm"
                          variant={selected ? "secondary" : "outline-primary"}
                          onClick={() =>
                            selected
                              ? removeDocumentFromMerge(
                                  documentsToMerge.indexOf(document)
                                )
                              : addDocumentToMerge(document)
                          }
                          disabled={mergingDocuments}
                        >
                          {selected ? "Remove" : "Select"}
                        </Button>
                      </span>
                    </div>
                  );
                })}
              </div>
              <Form.Label>Merge order</Form.Label>
              {documentsToMerge.length > 0 ? (
                <ol className="mb-0 ps-3">
                  {documentsToMerge.map((document, index) => (
                    <li
                      key={`${document.url}-${index}`}
                      className="d-flex align-items-center justify-content-between mb-2"
                    >
                      <span className="claim-document-merge-name">
                        {document.name}
                      </span>
                      <span className="claim-document-merge-actions">
                        <Button
                          size="sm"
                          variant="link"
                          className="p-1"
                          onClick={() =>
                            handlePreviewMergeDocument(document)
                          }
                          disabled={mergingDocuments}
                          aria-label={`Preview ${document.name}`}
                          title="Preview document"
                        >
                          <FaEye aria-hidden="true" />
                        </Button>
                        <Button
                          size="sm"
                          variant="link"
                          className="p-1"
                          onClick={() => moveDocumentToMerge(index, -1)}
                          disabled={mergingDocuments || index === 0}
                          aria-label={`Move ${document.name} up`}
                          title="Move up"
                        >
                          <FaArrowUp aria-hidden="true" />
                        </Button>
                        <Button
                          size="sm"
                          variant="link"
                          className="p-1"
                          onClick={() => moveDocumentToMerge(index, 1)}
                          disabled={
                            mergingDocuments ||
                            index === documentsToMerge.length - 1
                          }
                          aria-label={`Move ${document.name} down`}
                          title="Move down"
                        >
                          <FaArrowDown aria-hidden="true" />
                        </Button>
                      </span>
                    </li>
                  ))}
                </ol>
              ) : (
                <p className="mb-0">No documents selected.</p>
              )}
            </div>
            <section
              className="claim-document-merge-preview"
              aria-label="Merge document preview"
            >
              <h6>
                Preview
                {mergePreviewDocument ? `: ${mergePreviewDocument.name}` : ""}
              </h6>
              {mergePreviewLoading ? (
                <div className="claim-document-preview-status">
                  <Spinner animation="border" role="status" />
                  <span className="ms-2">Loading preview...</span>
                </div>
              ) : mergePreviewError ? (
                <Alert variant="warning">{mergePreviewError}</Alert>
              ) : mergePreviewUrl &&
                getDocumentPreviewType(mergePreviewDocument) === "image" ? (
                <img
                  className="claim-document-preview-image"
                  src={mergePreviewUrl}
                  alt={mergePreviewDocument.name}
                  onError={() =>
                    setMergePreviewError(
                      "This image could not be displayed in the preview."
                    )
                  }
                />
              ) : mergePreviewUrl &&
                getDocumentPreviewType(mergePreviewDocument) === "pdf" ? (
                <div className="claim-document-pdf-viewer">
                  {mergePreviewPageCount > 1 && (
                    <div className="claim-document-pdf-controls">
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() =>
                          setMergePreviewCurrentPage((page) =>
                            Math.max(1, page - 1)
                          )
                        }
                        disabled={mergePreviewCurrentPage <= 1}
                      >
                        Previous
                      </Button>
                      <span aria-live="polite">
                        Page {mergePreviewCurrentPage} of {mergePreviewPageCount}
                      </span>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() =>
                          setMergePreviewCurrentPage((page) =>
                            Math.min(mergePreviewPageCount, page + 1)
                          )
                        }
                        disabled={
                          mergePreviewCurrentPage >= mergePreviewPageCount
                        }
                      >
                        Next
                      </Button>
                    </div>
                  )}
                  <div
                    className="claim-document-pdf-page"
                    ref={mergePdfViewerRef}
                  >
                    <Document
                      file={mergePreviewUrl}
                      onLoadSuccess={({ numPages }) => {
                        setMergePreviewPageCount(numPages);
                        setMergePreviewCurrentPage(1);
                        setMergePreviewError("");
                      }}
                      onLoadError={(loadError) => {
                        console.error(
                          "Merge PDF preview rendering failed",
                          loadError
                        );
                        setMergePreviewError(
                          "The PDF could not be rendered. It may be damaged, encrypted, or use an unsupported feature."
                        );
                      }}
                      loading={
                        <div className="claim-document-preview-status">
                          <Spinner animation="border" role="status" />
                          <span className="ms-2">Rendering PDF preview...</span>
                        </div>
                      }
                      error={
                        <Alert variant="warning">
                          {mergePreviewError ||
                            "The PDF preview is unavailable."}
                        </Alert>
                      }
                    >
                      {mergePreviewPageCount > 0 && (
                        <Page
                          pageNumber={mergePreviewCurrentPage}
                          width={mergePreviewPageWidth}
                        />
                      )}
                    </Document>
                  </div>
                </div>
              ) : (
                <p className="claim-document-preview-empty">
                  Select a document to preview it here.
                </p>
              )}
            </section>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowMergeModal(false);
              clearMergePreview();
            }}
            disabled={mergingDocuments}
          >
            Cancel
          </Button>
          <Button
            onClick={handleMergeDocuments}
            disabled={mergingDocuments || documentsToMerge.length < 2}
          >
            {mergingDocuments ? (
              <Spinner animation="border" size="sm" />
            ) : (
              "Merge"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ClaimDocuments;